import { Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';
import { STRINGS } from '@/strings/strings';
import { asStringArray } from '@/features/story/types';

interface Props {
  learningQuestions: unknown;
}

/**
 * AA world-specific block. Renders the "Dein Kind will mehr wissen?"
 * box with a bulleted list of follow-up questions and a CTA pointing
 * to the Schlauberger Builder. Visual spec per PWA #16 Sub-Phase 16.3
 * Cluster B4 (locked Sebastian Gate-1 2026-04-29 + V2 verification):
 *
 *   - Container: navy-translucent bg + border (rgba(10,35,66,0.05) bg
 *     + rgba(10,35,66,0.1) border = `bg-dream-navy/5` + `border-dream-
 *     navy/10`). Identical token pair to SB Eltern-Box for system
 *     consistency.
 *   - Heading: `font-semibold` text-dream-navy with a leading Lucide
 *     `Lightbulb` icon (NOT 💡 emoji-as-icon — emoji may still be
 *     present in the heading string per Verbatim Content Rule).
 *   - Bullets: text-dream-navy.
 *   - CTA: `bg-dream-gold` filled, text-dream-navy, navigates to
 *     `/builder/sb` (current copy ends with "Schlauberger-Welt →").
 *     Gold reserved per visual hierarchy: gold = primary action,
 *     selected option = navy-bordered, etc.
 *
 * Renders inside the AA Story-Output story-card (16.3 Sub-Phase 4a),
 * after the story body. Gracefully hides itself when the field is
 * missing or malformed (older stories predating the learning-
 * questions prompt addition).
 */
export function StoryAALearningQuestions({ learningQuestions }: Props) {
  const items = asStringArray(learningQuestions);
  if (!items) return null;

  const copy = STRINGS.storyOutput.page.aaLearningQuestions;

  return (
    <div className="rounded-lg border border-dream-navy/10 bg-dream-navy/5 px-4 py-3">
      <div className="flex items-center gap-2">
        <Lightbulb aria-hidden className="h-5 w-5 shrink-0 text-dream-navy" />
        <p className="text-sm font-semibold text-dream-navy">{copy.heading}</p>
      </div>
      <ul className="mt-2 space-y-1 text-sm leading-relaxed text-dream-navy">
        {items.map((q) => (
          <li key={q} className="flex gap-2">
            <span aria-hidden>•</span>
            <span>{q}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/builder/sb"
        className="mt-3 inline-flex items-center justify-center rounded-md bg-dream-gold px-3 py-1.5 text-xs font-semibold text-dream-navy transition-colors hover:bg-dream-gold/90"
      >
        {copy.cta}
      </Link>
    </div>
  );
}
